import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL, WS_HUB_URL } from '../config';
import DocumentEditor from '../components/DocumentEditor';
import TopNavbar from '../components/TopNavbar';
import Comments from '../components/Comments';
import VersionHistory from '../components/VersionHistory';
import ErrorBoundary from '../components/ErrorBoundary';
import './ShareView.css';
import { getOrCreateUserId } from '../utils/userSession';

function ShareView() {
  const { documentId } = useParams();
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [userName, setUserName] = useState('');
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isEditingRef = useRef(false);
  const titleSaveTimerRef = useRef(null);

  useEffect(() => {
    const { userName: uname } = getOrCreateUserId();
    setUserName(uname);
  }, []);

  useEffect(() => {
    let active = true;
    const ws = new WebSocket(WS_HUB_URL);

    ws.onopen = () => { if (active) console.log('ShareView WebSocket Connected'); };

    ws.onmessage = (event) => {
      if (!active) return;
      try {
        const { type, payload } = JSON.parse(event.data);
        if (type === 'DocumentUpdated' && payload.documentId === documentId && !isEditingRef.current) {
          fetchDocument(documentId);
        }
      } catch (err) {
        console.error('WebSocket message parse error:', err);
      }
    };

    ws.onerror = () => { if (active) console.error('WebSocket error'); };

    return () => {
      active = false;
      ws.close();
    };
  }, [documentId]);

  useEffect(() => {
    fetchDocument(documentId);
  }, [documentId]);

  const fetchDocument = async (id) => {
    try {
      setIsLoading(true);
      const res = await fetch(`${API_URL}/api/document/${id}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedDoc(data);
      } else {
        setSelectedDoc(null);
      }
    } catch (err) {
      console.error('Error fetching document:', err);
      setSelectedDoc(null);
    } finally {
      setIsLoading(false);
    }
  };

  const updateDocument = async (id, title, content) => {
    try {
      const { userId, userName: uname } = getOrCreateUserId();
      const res = await fetch(`${API_URL}/api/document/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, content, userId, userName: uname })
      });
      if (res.ok) {
        const updated = await res.json();
        // Only update state if we're not actively editing
        if (!isEditingRef.current) {
          setSelectedDoc(updated);
        }
      }
    } catch (err) {
      console.error('Error updating document:', err);
    }
  };

  const handleTitleChange = (newTitle) => {
    if (selectedDoc) {
      isEditingRef.current = true;
      setSelectedDoc({ ...selectedDoc, title: newTitle });
      
      // Clear previous timer
      if (titleSaveTimerRef.current) {
        clearTimeout(titleSaveTimerRef.current);
      }
      
      // Debounce the actual save
      titleSaveTimerRef.current = setTimeout(async () => {
        await updateDocument(selectedDoc.id, newTitle, selectedDoc.content);
        isEditingRef.current = false;
      }, 500);
    }
  };

  if (isLoading) {
    return (
      <div className="share-loading">
        <p>Loading shared document...</p>
      </div>
    );
  }

  if (!selectedDoc) {
    return (
      <div className="share-error">
        <h2>Document not found</h2>
        <p>This shared document is no longer available.</p>
        <a href="/">← Back to DoodleDocs</a>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="share-wrapper">
        <TopNavbar 
          userName={userName} 
          documentTitle={selectedDoc.title ?? ''}
          onTitleChange={handleTitleChange}
          onShare={() => {}}
          onNewDoodle={() => window.location.href = '/'}
        />
        <div className="share-container">
          <div className="share-editor">
            {selectedDoc ? (
              <DocumentEditor
                document={selectedDoc}
                onUpdate={updateDocument}
                onToggleHistory={() => setShowVersionHistory(!showVersionHistory)}
                showHistory={showVersionHistory}
                onToggleComments={() => setShowComments(!showComments)}
                showComments={showComments}
              />
            ) : (
              <div className="no-doc">Loading...</div>
            )}
          </div>
          {selectedDoc && showVersionHistory && (
            <div className="version-history-panel">
              <VersionHistory documentId={selectedDoc.id} userName={userName} onClose={() => setShowVersionHistory(false)} />
            </div>
          )}
          {selectedDoc && showComments && (
            <div className="comments-panel-wrapper">
              <Comments documentId={selectedDoc.id} userName={userName} isOpen={showComments} onClose={() => setShowComments(false)} />
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default ShareView;
